import React, {useEffect, useMemo, useState} from "react";
import "../styles/schedule.css";
import {BASE_URLS} from "../config/urls";
import {SupplyDTO} from "../models/SupplyDTO";
import {DestinationWarehouse} from "../models/DestinationWarehouse";

import {OrderUpsertModal} from "../components/modal/OrderUpsertModal";
import {Company} from "../models/Company";
import {useAuth} from "../context/AuthContext";
import {apiFetch} from "../utils/api";
import {NeedAuthModal} from "../components/modal/NeedAuthModal";
import {NeedCompaniesModal} from "../components/modal/NeedCompaniesModal";
import {Person} from "../models/Person";

type SupplyModalState =
    | { open: false }
    | { open: true; supply: SupplyDTO };

type StoreFilter = "ALL" | "OZON" | "WB";

const parseSupply = (x: any): SupplyDTO =>
    new SupplyDTO(
        x.id,
        new Date(x.departureDate),
        new Date(x.acceptanceDate),
        x.title,
        (x.warehouses ?? []) as DestinationWarehouse[],
        !!x.isOzon
    );

const formatDate = (d: Date) =>
    d.toLocaleDateString("ru-RU", {day: "2-digit", month: "2-digit", year: "numeric"});

const detectStore = (s: SupplyDTO): StoreFilter => {
    if (s.isOzon) return "OZON";
    return "WB";
};

const storeLabel = (f: StoreFilter) => {
    switch (f) {
        case "ALL":
            return "Все";
        case "OZON":
            return "Ozon";
        case "WB":
            return "Wildberries";
    }
};

const storeChipLabel = (s: SupplyDTO) => {
    const st = detectStore(s);
    if (st === "OZON") return "Ozon";
    if (st === "WB") return "Wildberries";
    return "Поставка";
};

const SchedulePage: React.FC = () => {
    const {isAuth} = useAuth();

    const [items, setItems] = useState<SupplyDTO[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [person, setPerson] = useState<Person | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [query, setQuery] = useState("");
    const [storeFilter, setStoreFilter] = useState<StoreFilter>("ALL");

    const [modal, setModal] = useState<SupplyModalState>({open: false});
    const [needAuthOpen, setNeedAuthOpen] = useState(false);
    const [needCompaniesOpen, setNeedCompaniesOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchSupplies = async () => {
            setLoading(true);
            setError("");

            try {
                const resp = await fetch(`${BASE_URLS.api}/schedule`, {
                    headers: {Accept: "application/json"},
                    credentials: "include",
                });

                if (!resp.ok) throw new Error(`Не удалось загрузить расписание (HTTP ${resp.status})`);

                const data = await resp.json();
                console.log(data)
                const mapped = Array.isArray(data) ? data.map(parseSupply) : [];
                console.log(mapped)
                if (cancelled) return;
                setItems(mapped);
            } catch (e: any) {
                if (cancelled) return;
                setError(e?.message || "Ошибка загрузки расписания");
                setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchSupplies();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        isAuth
            ? fetch(`${BASE_URLS.api}/personal_account`, {
                credentials: "include",
                headers: {Accept: "application/json"},
            }).then((r) => r.json())
                .then((data) => {
                    if (cancelled) return;
                    setPerson(data);
                    setCompanies(Array.isArray(data.companies) ? data.companies : []);
                }).catch(() => undefined)
            : Promise.resolve(undefined);

        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return items
            .filter((s) => {
                if (storeFilter === "ALL") return true;
                return detectStore(s) === storeFilter;
            })
            .filter((s) => {
                if (!q) return true;

                const warehousesStr =
                    (s.warehouses ?? [])
                        .map((w: any) => (w?.title ?? w?.name ?? w?.warehouseName ?? "").toString())
                        .join(" ")
                        .toLowerCase();

                return (s.title ?? "").toLowerCase().includes(q) || warehousesStr.includes(q);
            })
            .sort((a, b) => a.departureDate.getTime() - b.departureDate.getTime());
    }, [items, query, storeFilter]);

    const tryOpenBooking = (supply: SupplyDTO) => {
        if (!person) {
            setNeedAuthOpen(true);
            return;
        }
        if (!companies?.length) {
            setNeedCompaniesOpen(true);
            return;
        }
        setModal({open: true, supply});
    };

    const closeOrderUpsert = () => setModal({open: false});


    return (
        <div className="schedule-page">
            <div className="schedule-hero">
                <div className="schedule-hero-card">
                    <h1 className="schedule-title">Расписание</h1>
                    <p className="schedule-subtitle">
                        Выберите поставку и нажмите “Записаться” — откроется форма создания заявки.
                    </p>

                    <div className="schedule-controls">
                        <div className="schedule-field">
                            <label className="schedule-label">Поиск</label>
                            <input
                                className="schedule-input"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Название или склад…"
                            />
                        </div>

                        <div className="schedule-field">
                            <label className="schedule-label">Маркетплейс</label>
                            <div className="schedule-filter-row">
                                {(["ALL", "OZON", "WB"] as StoreFilter[]).map((f) => (
                                    <button
                                        key={f}
                                        type="button"
                                        className={`schedule-pill ${storeFilter === f ? "is-active" : ""}`}
                                        onClick={() => setStoreFilter(f)}
                                    >
                                        {storeLabel(f)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="schedule-side">
                    <div className="schedule-side-card">
                        <div className="schedule-side-title">Подсказки</div>
                        <ul className="schedule-side-list">
                            <li>Список отсортирован по дате отправки.</li>
                            <li>Поиск работает по названию и складам.</li>
                            <li>Фильтр — Ozon / Wildberries.</li>
                            <li>Кнопка “Записаться” открывает форму создания заявки.</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="schedule-list">
                {loading && <div className="schedule-loading">
                  <div className="page_content">
                    <div className="spinner-wrapper" style={{ marginTop: 80 }}>
                      <div className="spinner"></div>
                    </div>
                  </div>
                </div>}
                {error && <div className="schedule-error">{error}</div>}

                {!loading && !error && filtered.length === 0 && (
                    <div className="schedule-empty">Ничего не найдено.</div>
                )}

                <div className="schedule-list-items">
                    {filtered.map((s) => {
                        const warehouses = (s.warehouses ?? []).map((w: any) => w?.warehouseName ?? "—");
                        const chip = storeChipLabel(s);

                        return (
                            <div key={s.id} className="schedule-item">
                                <div className="schedule-item-main">
                                    <div className="schedule-item-top">
                                        <span className="schedule-chip">{chip}</span>

                                        <div className="schedule-dates">
                                            <div className="schedule-date">
                                                <span className="schedule-date-label">Отправка:</span>
                                                <span
                                                    className="schedule-date-value">{formatDate(s.departureDate)}</span>
                                            </div>

                                            <span className="schedule-date-sep">•</span>

                                            <div className="schedule-date">
                                                <span className="schedule-date-label">Приёмка:</span>
                                                <span
                                                    className="schedule-date-value">{formatDate(s.acceptanceDate)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="schedule-item-title">{s.title}</div>

                                    <div className="schedule-item-meta">
                                        <div className="schedule-meta-label">Склады:</div>
                                        <div className="schedule-meta-value">
                                            {warehouses.length ? warehouses.join(", ") : "—"}
                                        </div>
                                    </div>
                                </div>

                                <div className="schedule-item-actions">
                                    <button type="button" className="schedule-primary-btn"
                                            onClick={() => tryOpenBooking(s)}>
                                        Записаться
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <NeedAuthModal
                isOpen={needAuthOpen}
                onClose={() => setNeedAuthOpen(false)}
            />

            <NeedCompaniesModal
                isOpen={needCompaniesOpen}
                onClose={() => setNeedCompaniesOpen(false)}
            />

            {person && modal.open &&
              <OrderUpsertModal
                isOpen={modal.open}
                onClose={closeOrderUpsert}
                supply={modal.supply} // ✅ supply нужен для складов
                companies={companies}
                person={person}
                onSaved={() => {
                    window.location.assign(`${BASE_URLS.app}/orders/current`);
                }}
              />}
        </div>
    );
};

export default SchedulePage;
